import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '../../environments/environment';
import {
  ApplicationPackage,
  ArtifactKind,
  CreatePackageRequest,
  HumanEditRequestBody,
  PackageListItem,
  PackageShowResponse,
  RedraftRequest,
  RefineRequest,
  SectionKey,
  TailorTurnResponse,
} from '../interfaces/application-package';

@Injectable({
  providedIn: 'root'
})
export class TailorService {

  private readonly httpOptions = {
    withCredentials: true
  };

  constructor(private http: HttpClient) {}

  public createPackage(userId: number, body: CreatePackageRequest): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/users/${userId}/application-packages`,
      body,
      { ...this.httpOptions }
    );
  }

  public refineTurn(packageId: number, body: RefineRequest): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/turns`,
      body,
      this.httpOptions
    );
  }

  public finalize(packageId: number, kind: ArtifactKind): Observable<ApplicationPackage> {
    return this.http.post<ApplicationPackage>(
      `${env.api}/application-packages/${packageId}/finalize`,
      { kind },
      this.httpOptions
    );
  }

  public submit(packageId: number): Observable<ApplicationPackage> {
    return this.http.post<ApplicationPackage>(
      `${env.api}/application-packages/${packageId}/submit`,
      {},
      this.httpOptions
    );
  }

  public returnWork(packageId: number): Observable<ApplicationPackage> {
    return this.http.post<ApplicationPackage>(
      `${env.api}/application-packages/${packageId}/return`,
      {},
      this.httpOptions
    );
  }

  public discard(packageId: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(
      `${env.api}/application-packages/${packageId}`,
      this.httpOptions
    );
  }

  public getPackage(packageId: number): Observable<PackageShowResponse> {
    return this.http.get<PackageShowResponse>(
      `${env.api}/application-packages/${packageId}`,
      this.httpOptions
    );
  }

  public listForUser(userId: number): Observable<PackageListItem[]> {
    return this.http.get<PackageListItem[]>(
      `${env.api}/users/${userId}/application-packages`,
      this.httpOptions
    );
  }

  public listForClient(clientId: number): Observable<PackageListItem[]> {
    return this.http.get<PackageListItem[]>(
      `${env.api}/clients/${clientId}/application-packages`,
      this.httpOptions
    );
  }

  public preview(packageId: number): Observable<string> {
    return this.http.get(
      `${env.api}/application-packages/${packageId}/preview`,
      { ...this.httpOptions, responseType: 'text' }
    );
  }

  /** The stored letter rendered in the tenant's letter template (HTML, 404 when the package has no letter). */
  public previewLetter(packageId: number): Observable<string> {
    return this.http.get(
      `${env.api}/application-packages/${packageId}/preview/letter`,
      { ...this.httpOptions, responseType: 'text' }
    );
  }

  public downloadUrl(packageId: number, kind: 'cv' | 'letter'): string {
    return `${env.api}/application-packages/${packageId}/download/${kind}`;
  }

  public restoreRole(packageId: number, roleId: number): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/roles/${roleId}/restore`, {}, this.httpOptions);
  }

  public setAsideRole(packageId: number, roleId: number): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/roles/${roleId}/set-aside`, {}, this.httpOptions);
  }

  public setAsideItem(packageId: number, section: SectionKey, itemId: number): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/sections/${section}/items/${itemId}/set-aside`, {}, this.httpOptions);
  }

  public restoreItem(packageId: number, section: SectionKey, itemId: number): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/sections/${section}/items/${itemId}/restore`, {}, this.httpOptions);
  }

  public setAsideEducation(packageId: number, eduId: number): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/education/${eduId}/set-aside`, {}, this.httpOptions);
  }

  public restoreEducation(packageId: number, eduId: number): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/education/${eduId}/restore`, {}, this.httpOptions);
  }

  public setEducationDescription(
    packageId: number, eduId: number, body: { show: boolean; text?: string }
  ): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/education/${eduId}/description`, body, this.httpOptions);
  }

  public redraft(packageId: number, body: RedraftRequest): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/redraft`, body, this.httpOptions);
  }

  // Like acceptProposal, the controller's edit() delegates to show(): an edit
  // adds a conversation turn, and only the show() envelope carries it.
  public edit(packageId: number, body: HumanEditRequestBody): Observable<PackageShowResponse> {
    return this.http.post<PackageShowResponse>(
      `${env.api}/application-packages/${packageId}/edits`, body, this.httpOptions);
  }

  /**
   * Change the CV template only, no agent re-run. Fast: the server swaps the
   * template fields on the current draft and re-renders. See #723.
   */
  public setTemplate(
    packageId: number,
    body: { template_id: string; template_type?: string; client_cv_template_id?: number | null }
  ): Observable<TailorTurnResponse> {
    return this.http.post<TailorTurnResponse>(
      `${env.api}/application-packages/${packageId}/template`, body, this.httpOptions);
  }

  // The controller's acceptProposal delegates to show(), so this returns the
  // same envelope shape as getPackage().
  public acceptProposal(packageId: number, proposalId: number): Observable<PackageShowResponse> {
    return this.http.post<PackageShowResponse>(
      `${env.api}/application-packages/${packageId}/proposals/${proposalId}/accept`,
      {},
      this.httpOptions
    );
  }

  // rejectProposal now delegates to show() as well (mirrors acceptProposal), so
  // the frontend can refresh coverage + clear the pending proposal after a reject.
  public rejectProposal(packageId: number, proposalId: number): Observable<PackageShowResponse> {
    return this.http.post<PackageShowResponse>(
      `${env.api}/application-packages/${packageId}/proposals/${proposalId}/reject`,
      {},
      this.httpOptions
    );
  }
}
